import React, { useState, useCallback } from 'react'
import { 
  Plus, 
  Eye, 
  Save, 
  Download, 
  Upload,
  Palette,
  Layout,
  Settings,
  Globe,
  ChevronUp,
  ChevronDown,
  Trash2,
  Search,
  Monitor,
  Smartphone,
  Tablet,
  Sparkles,
  Check,
  Music
} from 'lucide-react'
import SEOPanel from './SEOPanel'
import ResponsivePanel from './ResponsivePanel'
import TemplateSelector, { Template as TemplateSelectorTemplate } from './Templates/TemplateSelector'
import TemplateRenderer from './Templates/TemplateRenderer'
import MusicCreatorPro from './Templates/MusicCreatorPro'
import VisualArtistStudio from './Templates/VisualArtistStudio'
import CreatorHub from './Templates/CreatorHub'

// Types
interface WebsiteComponent {
  id: string
  type: 'hero' | 'gallery' | 'contact' | 'pricing' | 'blog' | 'portfolio'
  data: any
  styles: React.CSSProperties
  order: number
}

interface SEOData {
  title: string
  description: string
  keywords: string[]
  author: string
  ogTitle: string
  ogDescription: string
  ogImage: string
  twitterCard: string
  twitterSite: string
  canonical: string
  robots: string
  language: string
  favicon: string
  structuredData: {
    type: string
    name: string
    description: string
    url: string
    image: string
    author: {
      name: string
      url: string
    }
  }
}

interface ResponsiveSettings {
  breakpoints: {
    mobile: number
    tablet: number
    desktop: number
  }
  mobileSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
  tabletSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
  desktopSettings: {
    fontSize: number
    spacing: number
    hideElements: string[]
    stackElements: string[]
  }
}

interface WebsiteData {
  id?: string
  title: string
  description: string
  components: WebsiteComponent[]
  styles: {
    primaryColor: string
    secondaryColor: string
    fontFamily: string
    backgroundColor: string
  }
  seo: SEOData
  responsive: ResponsiveSettings
  domain?: string
  published: boolean
}

// Use the Template type from TemplateSelector
type Template = TemplateSelectorTemplate

// Mock templates - simplified for compatibility
const templates: Template[] = [
  {
    id: 'artist-portfolio',
    name: 'Artist Portfolio',
    category: 'artist',
    description: 'Beautiful portfolio template for digital artists',
    preview: '/templates/artist-preview.jpg',
    features: ['Gallery', 'Bio Section', 'Contact Form'],
    icon: <Palette className="w-8 h-8" />,
    colors: ['#8B5CF6', '#F59E0B'],
    components: [
      {
        type: 'hero',
        data: {
          title: 'Welcome to My Art Gallery',
          subtitle: 'Digital Artist & Creative Visionary',
          backgroundImage: '',
          ctaButton: { text: 'View Gallery', link: '#gallery' }
        },
        styles: { padding: '80px 20px', textAlign: 'center' as const },
        order: 0
      },
      {
        type: 'gallery',
        data: {
          images: [],
          layout: 'masonry' as const,
          captions: true
        },
        styles: { padding: '60px 20px' },
        order: 1
      },
      {
        type: 'contact',
        data: {
          email: 'artist@example.com',
          socialLinks: [
            { platform: 'twitter', url: 'https://twitter.com/artist' },
            { platform: 'instagram', url: 'https://instagram.com/artist' }
          ],
          contactForm: true
        },
        styles: { padding: '60px 20px', backgroundColor: '#f8f9fa' },
        order: 2
      }
    ],
    styles: {
      primaryColor: '#8B5CF6',
      secondaryColor: '#F59E0B',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#FFFFFF'
    }
  },
  {
    id: 'musician-site',
    name: 'Musician Site',
    category: 'music',
    description: 'Professional template for musicians and producers',
    preview: '/templates/musician-preview.jpg',
    features: ['Music Player', 'Tour Dates', 'Social Links'],
    icon: <Music className="w-8 h-8" />,
    colors: ['#EF4444', '#F59E0B'],
    components: [
      {
        type: 'hero',
        data: {
          title: 'Music That Moves You',
          subtitle: 'Singer-Songwriter & Producer',
          backgroundImage: '',
          ctaButton: { text: 'Listen Now', link: '#music' }
        },
        styles: { padding: '80px 20px', textAlign: 'center' as const },
        order: 0
      },
      {
        type: 'pricing',
        data: {
          plans: [
            { name: 'Basic', price: 5, features: ['Access to music', 'Updates'], currency: 'SUI' },
            { name: 'Premium', price: 15, features: ['All music', 'Exclusive content', 'Behind scenes'], currency: 'SUI' }
          ]
        },
        styles: { padding: '60px 20px' },
        order: 1
      }
    ],
    styles: {
      primaryColor: '#EF4444',
      secondaryColor: '#F59E0B',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#000000'
    }
  }
]

export default function WebsiteBuilder() {
  const [showTemplateSelector, setShowTemplateSelector] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateSelectorTemplate | null>(null)
  const [previewTemplate, setPreviewTemplate] = useState<TemplateSelectorTemplate | null>(null)
  const [websiteData, setWebsiteData] = useState<WebsiteData>({
    title: 'My Website',
    description: 'Built with Harmony Hub',
    components: [],
    styles: {
      primaryColor: '#8B5CF6',
      secondaryColor: '#F59E0B',
      fontFamily: 'Inter, sans-serif',
      backgroundColor: '#FFFFFF'
    },
    seo: {
      title: 'My Website - Built with Harmony Hub',
      description: 'A beautiful website built with Harmony Hub, the decentralized creator platform.',
      keywords: ['website', 'harmony hub', 'decentralized', 'creator'],
      author: 'Creator',
      ogTitle: 'My Website - Built with Harmony Hub',
      ogDescription: 'A beautiful website built with Harmony Hub, the decentralized creator platform.',
      ogImage: '',
      twitterCard: 'summary_large_image',
      twitterSite: '@harmonyhub',
      canonical: '',
      robots: 'index, follow',
      language: 'en',
      favicon: '',
      structuredData: {
        type: 'WebPage',
        name: 'My Website',
        description: 'A beautiful website built with Harmony Hub',
        url: '',
        image: '',
        author: {
          name: 'Creator',
          url: ''
        }
      }
    },
    responsive: {
      breakpoints: {
        mobile: 768,
        tablet: 1024,
        desktop: 1200
      },
      mobileSettings: {
        fontSize: 14,
        spacing: 16,
        hideElements: [],
        stackElements: []
      },
      tabletSettings: {
        fontSize: 16,
        spacing: 24,
        hideElements: [],
        stackElements: []
      },
      desktopSettings: {
        fontSize: 18,
        spacing: 32,
        hideElements: [],
        stackElements: []
      }
    },
    published: false
  })

  const [previewMode, setPreviewMode] = useState(false)
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null)
  const [activePanel, setActivePanel] = useState<'design' | 'seo' | 'responsive'>('design')
  const [responsivePreviewMode, setResponsivePreviewMode] = useState<'desktop' | 'tablet' | 'mobile'>('desktop')

  // Handle template selection
  const handleTemplateSelect = useCallback((template: TemplateSelectorTemplate) => {
    setSelectedTemplate(template)
    setWebsiteData(prev => ({
      ...prev,
      title: `${template.name} Website`,
      description: template.description,
      components: template.components || [],
      styles: {
        ...prev.styles,
        ...template.styles
      }
    }))
  }, [])

  // Handle template preview
  const handleTemplatePreview = useCallback((template: TemplateSelectorTemplate) => {
    setPreviewTemplate(template)
  }, [])

  // Handle component reordering
  const moveComponent = useCallback((index: number, direction: 'up' | 'down') => {
    const newComponents = [...websiteData.components]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    
    if (targetIndex >= 0 && targetIndex < newComponents.length) {
      [newComponents[index], newComponents[targetIndex]] = [newComponents[targetIndex], newComponents[index]]
      
      // Update order
      const updatedComponents = newComponents.map((comp, idx) => ({
        ...comp,
        order: idx
      }))

      setWebsiteData(prev => ({
        ...prev,
        components: updatedComponents
      }))
    }
  }, [websiteData.components])

  // Add new component
  const addComponent = useCallback((type: WebsiteComponent['type']) => {
    const newComponent: WebsiteComponent = {
      id: `comp-${Date.now()}`,
      type,
      data: {},
      styles: { padding: '40px 20px' },
      order: websiteData.components.length
    }

    setWebsiteData(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }))
  }, [websiteData.components.length])

  // Update component data
  const updateComponent = useCallback((componentId: string, updates: Partial<WebsiteComponent>) => {
    setWebsiteData(prev => ({
      ...prev,
      components: prev.components.map(comp =>
        comp.id === componentId ? { ...comp, ...updates } : comp
      )
    }))
  }, [])

  // Generate HTML from website data
  const generateHTML = useCallback(() => {
    const { title, description, components, styles } = websiteData
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <meta name="description" content="${description}">
    <link href="https://fonts.googleapis.com/css2?family=${styles.fontFamily.replace(' ', '+')}:wght@300;400;500;600;700&display=swap" rel="stylesheet">
    <style>
        :root {
            --primary-color: ${styles.primaryColor};
            --secondary-color: ${styles.secondaryColor};
            --background-color: ${styles.backgroundColor};
            --font-family: ${styles.fontFamily};
        }
        
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }
        
        body {
            font-family: var(--font-family);
            background-color: var(--background-color);
            line-height: 1.6;
            color: #333;
        }
        
        .container {
            max-width: 1200px;
            margin: 0 auto;
            padding: 0 20px;
        }
        
        .hero {
            background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
            color: white;
            padding: 100px 20px;
            text-align: center;
        }
        
        .hero h1 {
            font-size: 3rem;
            margin-bottom: 1rem;
            font-weight: 700;
        }
        
        .hero p {
            font-size: 1.2rem;
            margin-bottom: 2rem;
            opacity: 0.9;
        }
        
        .btn {
            display: inline-block;
            padding: 12px 30px;
            background-color: var(--secondary-color);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-weight: 500;
            transition: transform 0.2s;
        }
        
        .btn:hover {
            transform: translateY(-2px);
        }
        
        .section {
            padding: 80px 20px;
        }
        
        .gallery {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 20px;
            padding: 40px 0;
        }
        
        .gallery-item {
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
            transition: transform 0.2s;
        }
        
        .gallery-item:hover {
            transform: translateY(-5px);
        }
        
        .contact-form {
            max-width: 600px;
            margin: 0 auto;
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }
        
        .form-group {
            margin-bottom: 20px;
        }
        
        .form-group label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
        }
        
        .form-group input,
        .form-group textarea {
            width: 100%;
            padding: 12px;
            border: 2px solid #e5e7eb;
            border-radius: 6px;
            font-family: inherit;
        }
        
        .form-group input:focus,
        .form-group textarea:focus {
            outline: none;
            border-color: var(--primary-color);
        }
        
        @media (max-width: 768px) {
            .hero h1 {
                font-size: 2rem;
            }
            
            .hero p {
                font-size: 1rem;
            }
        }
    </style>
</head>
<body>
    ${components.map(component => {
      switch (component.type) {
        case 'hero':
          return `
            <section class="hero" style="${Object.entries(component.styles).map(([key, value]) => 
              `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`
            ).join('; ')}">
              <div class="container">
                <h1>${component.data.title || 'Welcome'}</h1>
                <p>${component.data.subtitle || 'Your subtitle here'}</p>
                ${component.data.ctaButton ? `<a href="${component.data.ctaButton.link}" class="btn">${component.data.ctaButton.text}</a>` : ''}
              </div>
            </section>
          `
        case 'gallery':
          return `
            <section class="section" style="${Object.entries(component.styles).map(([key, value]) => 
              `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`
            ).join('; ')}">
              <div class="container">
                <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem;">Gallery</h2>
                <div class="gallery">
                  ${component.data.images?.map((img: any, index: number) => `
                    <div class="gallery-item">
                      <img src="${img.url}" alt="${img.alt || `Gallery item ${index + 1}`}" style="width: 100%; height: 250px; object-fit: cover;">
                      ${component.data.captions ? `<p style="padding: 15px; text-align: center;">${img.caption || ''}</p>` : ''}
                    </div>
                  `).join('') || '<p style="text-align: center; color: #666;">No images yet. Add some to see your gallery!</p>'}
                </div>
              </div>
            </section>
          `
        case 'contact':
          return `
            <section class="section" style="${Object.entries(component.styles).map(([key, value]) => 
              `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`
            ).join('; ')}">
              <div class="container">
                <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem;">Contact</h2>
                <div class="contact-form">
                  <form>
                    <div class="form-group">
                      <label for="name">Name</label>
                      <input type="text" id="name" name="name" required>
                    </div>
                    <div class="form-group">
                      <label for="email">Email</label>
                      <input type="email" id="email" name="email" required>
                    </div>
                    <div class="form-group">
                      <label for="message">Message</label>
                      <textarea id="message" name="message" rows="5" required></textarea>
                    </div>
                    <button type="submit" class="btn" style="width: 100%;">Send Message</button>
                  </form>
                </div>
              </div>
            </section>
          `
        case 'pricing':
          return `
            <section class="section" style="${Object.entries(component.styles).map(([key, value]) => 
              `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`
            ).join('; ')}">
              <div class="container">
                <h2 style="text-align: center; margin-bottom: 40px; font-size: 2.5rem;">Pricing</h2>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 30px;">
                  ${component.data.plans?.map((plan: any, index: number) => `
                    <div style="background: white; padding: 40px; border-radius: 12px; box-shadow: 0 8px 25px rgba(0,0,0,0.1); text-align: center;">
                      <h3 style="font-size: 1.5rem; margin-bottom: 20px;">${plan.name}</h3>
                      <div style="font-size: 3rem; font-weight: 700; color: var(--primary-color); margin-bottom: 20px;">
                        ${plan.price} ${plan.currency}
                      </div>
                      <ul style="list-style: none; margin-bottom: 30px;">
                        ${plan.features.map((feature: string) => `<li style="padding: 5px 0;">✓ ${feature}</li>`).join('')}
                      </ul>
                      <button class="btn" style="width: 100%;">Choose Plan</button>
                    </div>
                  `).join('') || '<p style="text-align: center; color: #666;">No pricing plans yet.</p>'}
                </div>
              </div>
            </section>
          `
        default:
          return `<section style="padding: 40px 20px; text-align: center;"><h2>${component.type.charAt(0).toUpperCase() + component.type.slice(1)} Section</h2></section>`
      }
    }).join('')}
    
    <footer style="background: #1f2937; color: white; padding: 40px 20px; text-align: center;">
      <div class="container">
        <p>&copy; 2024 ${title}. Built with Harmony Hub.</p>
      </div>
    </footer>
</body>
</html>
    `
  }, [websiteData])

  // Save website
  const handleSave = useCallback(async () => {
    try {
      const html = generateHTML()
      console.log('Saving website:', { websiteData, html })
      // TODO: Upload to Walrus and save metadata to Sui
      alert('Website saved successfully! 🎉')
    } catch (error) {
      console.error('Error saving website:', error)
      alert('Error saving website. Please try again.')
    }
  }, [websiteData, generateHTML])

  // Publish website
  const handlePublish = useCallback(async () => {
    try {
      const html = generateHTML()
      console.log('Publishing website:', { websiteData, html })
      // TODO: Publish to Walrus and make live
      setWebsiteData(prev => ({ ...prev, published: true }))
      alert('Website published! 🚀 Your site is now live!')
    } catch (error) {
      console.error('Error publishing website:', error)
      alert('Error publishing website. Please try again.')
    }
  }, [websiteData, generateHTML])

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-900">🌐 Website Builder</h1>
            <span className="text-sm text-gray-500">Build your creator empire</span>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Eye className="w-4 h-4 mr-2" />
              {previewMode ? 'Edit' : 'Preview'}
            </button>
            
            <button
              onClick={handleSave}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save
            </button>
            
            <button
              onClick={handlePublish}
              className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gold-500 rounded-lg hover:bg-gold-600"
            >
              <Globe className="w-4 h-4 mr-2" />
              Publish
            </button>
          </div>
        </div>

        {/* Panel Navigation */}
        <div className="flex items-center space-x-1 mt-4">
          <button
            onClick={() => setActivePanel('design')}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              activePanel === 'design'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Layout className="w-4 h-4 mr-2" />
            Design
          </button>
          <button
            onClick={() => setActivePanel('seo')}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              activePanel === 'seo'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Search className="w-4 h-4 mr-2" />
            SEO
          </button>
          <button
            onClick={() => setActivePanel('responsive')}
            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              activePanel === 'responsive'
                ? 'bg-purple-100 text-purple-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Smartphone className="w-4 h-4 mr-2" />
            Responsive
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 h-screen overflow-y-auto">
          {activePanel === 'design' && (
            <>
              {!selectedTemplate ? (
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Template</h2>
              <div className="space-y-4">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="cursor-pointer border border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all"
                  >
                    <div className="aspect-video bg-gray-100 rounded mb-3 flex items-center justify-center">
                      <span className="text-gray-500">🎨</span>
                    </div>
                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{template.category}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Components</h2>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Change Template
                </button>
              </div>

              {/* Add Components */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Add Component</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['hero', 'gallery', 'contact', 'pricing'].map((type) => (
                    <button
                      key={type}
                      onClick={() => addComponent(type as WebsiteComponent['type'])}
                      className="flex items-center justify-center px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Component List */}
              <div className="space-y-2">
                {websiteData.components.map((component, index) => (
                  <div
                    key={component.id}
                    className={`p-3 border rounded-lg ${
                      selectedComponent === component.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => setSelectedComponent(component.id)}
                      >
                        <span className="text-sm font-medium text-gray-900">
                          {component.type.charAt(0).toUpperCase() + component.type.slice(1)}
                        </span>
                        <span className="text-xs text-gray-500 ml-2">#{index + 1}</span>
                      </div>
                      
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => moveComponent(index, 'up')}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => moveComponent(index, 'down')}
                          disabled={index === websiteData.components.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setWebsiteData(prev => ({
                              ...prev,
                              components: prev.components.filter(c => c.id !== component.id)
                            }))
                          }}
                          className="p-1 text-red-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </>)}

          {activePanel === 'seo' && (
            <div className="p-6">
              <SEOPanel
                seoData={websiteData.seo}
                onUpdate={(seoData) => setWebsiteData(prev => ({ ...prev, seo: seoData }))}
                websiteUrl={websiteData.domain}
                previewMode={responsivePreviewMode}
                onPreviewModeChange={setResponsivePreviewMode}
              />
            </div>
          )}

          {activePanel === 'responsive' && (
            <div className="p-6">
              <ResponsivePanel
                responsiveSettings={websiteData.responsive}
                onUpdate={(responsiveSettings) => setWebsiteData(prev => ({ ...prev, responsive: responsiveSettings }))}
                previewMode={responsivePreviewMode}
                onPreviewModeChange={setResponsivePreviewMode}
                websiteComponents={websiteData.components}
                onComponentUpdate={(componentId, updates) => {
                  setWebsiteData(prev => ({
                    ...prev,
                    components: prev.components.map(comp =>
                      comp.id === componentId ? { ...comp, ...updates } : comp
                    )
                  }))
                }}
              />
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {previewMode ? (
            <div className="h-screen overflow-y-auto">
              {selectedTemplate ? (
                <div className="w-full h-full overflow-auto">
                  <TemplateRenderer template={selectedTemplate} />
                </div>
              ) : (
                <iframe
                  srcDoc={generateHTML()}
                  className="w-full h-full border-0"
                  title="Website Preview"
                />
              )}
            </div>
          ) : (
            <div className="p-6">
              <div className="max-w-4xl mx-auto">
                {selectedTemplate ? (
                  <div className="text-center py-20">
                    <div className="text-6xl mb-4">🎨</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      {selectedTemplate.name} Template Selected
                    </h2>
                    <p className="text-gray-600 mb-8">
                      {selectedTemplate.description}
                    </p>
                    <div className="bg-gray-50 rounded-lg p-6 mb-6 max-w-md mx-auto">
                      <h3 className="font-semibold text-gray-900 mb-3">Template Features:</h3>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {selectedTemplate.features.map((feature, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-4 justify-center">
                      <button
                        onClick={() => setPreviewMode(true)}
                        className="px-6 py-3 bg-gold-500 text-black rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2"
                      >
                        <Eye className="w-5 h-5" />
                        Preview Template
                      </button>
                      <button
                        onClick={() => setSelectedTemplate(null)}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        Choose Different Template
                      </button>
                    </div>
                  </div>
                ) : websiteData.components.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="text-6xl mb-4">🌐</div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                      Start Building Your Website
                    </h2>
                    <p className="text-gray-600 mb-8">
                      Choose a template to get started, or build from scratch
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <button
                        onClick={() => setShowTemplateSelector(true)}
                        className="px-8 py-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center gap-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <Sparkles className="w-6 h-6" />
                        Choose Template
                      </button>
                      <button
                        onClick={() => {
                          // Start building from scratch
                          setWebsiteData(prev => ({
                            ...prev,
                            components: []
                          }))
                        }}
                        className="px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all duration-300 flex items-center gap-3 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105"
                      >
                        <Plus className="w-6 h-6" />
                        Build from Scratch
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {websiteData.components.map((component, index) => (
                      <div
                        key={component.id}
                        className={`border-2 rounded-lg p-6 ${
                          selectedComponent === component.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200'
                        }`}
                        onClick={() => setSelectedComponent(component.id)}
                      >
                        <div className="mb-4 flex items-center justify-between">
                          <h3 className="text-lg font-medium text-gray-900">
                            {component.type.charAt(0).toUpperCase() + component.type.slice(1)} Section
                          </h3>
                          <span className="text-sm text-gray-500">#{index + 1}</span>
                        </div>
                        
                        <div className="bg-gray-100 rounded-lg p-8 text-center">
                          <div className="text-4xl mb-2">
                            {component.type === 'hero' && '🎯'}
                            {component.type === 'gallery' && '🖼️'}
                            {component.type === 'contact' && '📧'}
                            {component.type === 'pricing' && '💰'}
                          </div>
                          <p className="text-gray-600">
                            {component.type} component preview
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

             {/* Template Selector Modal */}
             {showTemplateSelector && (
               <TemplateSelector
                 onSelectTemplate={(template) => {
                   handleTemplateSelect(template)
                   setShowTemplateSelector(false)
                 }}
                 onPreviewTemplate={handleTemplatePreview}
                 onClose={() => setShowTemplateSelector(false)}
               />
             )}

             {/* Template Preview Modal */}
             {previewTemplate && (
               <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                 <div className="bg-gray-900 rounded-2xl border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-hidden">
                   {/* Preview Header */}
                   <div className="p-6 border-b border-white/20">
                     <div className="flex items-center justify-between">
                       <div>
                         <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Georgia, serif' }}>
                           {previewTemplate.name} Preview
                         </h2>
                         <p className="text-purple-200">{previewTemplate.description}</p>
                       </div>
                       <button
                         onClick={() => setPreviewTemplate(null)}
                         className="text-white/60 hover:text-white text-2xl"
                       >
                         ×
                       </button>
                     </div>
                   </div>

                   {/* Preview Content */}
                   <div className="flex-1 overflow-y-auto">
                     <TemplateRenderer template={previewTemplate} />
                   </div>

                   {/* Preview Footer */}
                   <div className="p-6 border-t border-white/20 flex items-center justify-between">
                     <div className="text-purple-200 text-sm">
                       Preview of {previewTemplate.name} template
                     </div>
                     <div className="flex gap-3">
                       <button
                         onClick={() => setPreviewTemplate(null)}
                         className="px-6 py-2 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors"
                       >
                         Close Preview
                       </button>
                       <button
                         onClick={() => {
                           handleTemplateSelect(previewTemplate)
                           setPreviewTemplate(null)
                           setShowTemplateSelector(false)
                         }}
                         className="px-6 py-2 bg-gold-500 text-black rounded-lg hover:bg-gold-600 transition-colors flex items-center gap-2"
                       >
                         <Check className="w-4 h-4" />
                         Use This Template
                       </button>
                     </div>
                   </div>
                 </div>
               </div>
             )}
    </div>
  )
}
